import type { AssetClassBranch } from '@interfaces/financialProduct';

import { TextField } from '@ovComponents/2-component/textField/textField';
import { Box, Grid, IconButton, Typography } from 'hephaestus/web';
import { RemoveCircleOutlined as RemoveCircleOutlineIcon } from 'hephaestus/webIcons';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AssetClassSelect,
  type AssetClassSelectItem,
} from '../assetClassSelect/assetClassSelect';

export const AssetClassBranchesSelect = ({
  assetClassBranches,
  setAssetClassBranches,
  testId = 'asset-class-branches-select',
}: {
  assetClassBranches: AssetClassBranch[];
  setAssetClassBranches: (branches: AssetClassBranch[]) => void;
  testId?: string;
}) => {
  const { t } = useTranslation(['modelPortfolios', 'components']);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleAssetClassSelectFull =
    (id: string) => (item: AssetClassSelectItem) => {
      setAssetClassBranches(
        assetClassBranches.map((branch) =>
          branch.id === id
            ? {
                ...branch,
                assetClass: {
                  id: item.id,
                  key: item.key ?? '',
                  name: item.name ?? '',
                  translatedName: item.translatedName,
                },
              }
            : branch,
        ),
      );
    };

  const handlePercentageChange =
    (id: string) => (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      // Only allow numbers and empty string
      if (
        value === '' ||
        (/^\d*\.?\d*$/.test(value) && parseFloat(value) <= 100)
      ) {
        setAssetClassBranches(
          assetClassBranches.map((branch) =>
            branch.id === id
              ? {
                  ...branch,
                  targetPercentage: value === '' ? null : parseFloat(value),
                }
              : branch,
          ),
        );
      }
    };

  const addBranch = () => {
    const newBranch = {
      id: String(Date.now()),
      assetClass: { id: '', key: '', name: '' },
      targetPercentage: null,
      color: 'supportOne',
    };
    setAssetClassBranches([
      ...assetClassBranches,
      newBranch as AssetClassBranch,
    ]);
  };

  const removeBranch = (id: string) => {
    setAssetClassBranches(
      assetClassBranches.filter((branch) => branch.id !== id),
    );
  };

  const usedAssetClasses = assetClassBranches
    .map((branch) => branch.assetClass?.id)
    .filter((id): id is string => Boolean(id));

  return (
    <Box
      data-testid={`${testId}-box`}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
      }}
    >
      {assetClassBranches.map((branch) => (
        <Grid container spacing={2} key={branch.id} sx={{ mb: 2 }}>
          <Grid size={8}>
            <AssetClassSelect
              testId={`${testId}-asset-class-select-${branch.id}`}
              leadingIcon='search'
              value={branch.assetClass?.id || ''}
              setAssetClass={() => {}}
              onSelectFull={handleAssetClassSelectFull(branch.id)}
              label={t('modelPortfolios:assetClass')}
              filteredAllocations={usedAssetClasses}
              omitAny={true}
              required
            />
          </Grid>
          <Grid size={3}>
            <TextField
              testId={`${testId}-text-field-${branch.id}`}
              label={t('modelPortfolios:securityTableColumns.targetPercentage')}
              value={branch.targetPercentage}
              onChange={handlePercentageChange(branch.id)}
              onFocus={() => setFocusedField(branch.id)}
              onBlur={() => setFocusedField(null)}
              type='number'
              fullWidth
              required
              error={
                focusedField === branch.id &&
                (branch.targetPercentage === 0 ||
                  branch.targetPercentage === null ||
                  branch.targetPercentage > 100)
              }
              trailingIcon='percent'
            />
          </Grid>
          <Grid
            sx={{
              display: 'flex',
              alignItems:
                focusedField === branch.id &&
                (branch.targetPercentage === 0 ||
                  branch.targetPercentage === null ||
                  branch.targetPercentage > 100)
                  ? 'center'
                  : 'flex-end',
              mb:
                focusedField === branch.id &&
                (branch.targetPercentage === 0 ||
                  branch.targetPercentage === null ||
                  branch.targetPercentage > 100)
                  ? 0
                  : 0.5,
            }}
            size={1}
          >
            {assetClassBranches.length > 1 && (
              <IconButton
                data-testid={`${testId}-delete-icon-button-${branch.id}`}
                onClick={() => removeBranch(branch.id)}
                size='xsmall'
                sx={{
                  color: 'GrayText',
                }}
                aria-label={t('components:portfolioTable.deleteAssetClass')}
              >
                <RemoveCircleOutlineIcon fontSize='inherit' />
              </IconButton>
            )}
          </Grid>
        </Grid>
      ))}
      <Typography
        variant='bodySmall'
        component='p'
        data-testid={`${testId}-add-asset-class-branch-button`}
        sx={{
          fontSize: '12px',
          fontWeight: 500,
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8,
          },
          color: 'primary.main',
          pb: 2,
          display: 'inline-block', // This is to prevent the typography from taking up the full width of the container
        }}
        onClick={addBranch}
      >
        {t('addAssetClassBranch')}
      </Typography>

      <Box
        data-testid={`${testId}-total-box`}
        sx={{
          mr: 8,
          display: 'inline-flex',
          alignItems: 'center',
          p: 1.5,
          pt: 2,
          float: 'right',
        }}
      >
        <Typography
          variant='bodyMedium'
          sx={{ mr: 1, color: 'grey.500', fontWeight: 500 }}
          data-testid={`${testId}-total-label`}
        >
          {t('components:portfolioTable.total')}
        </Typography>
        <Typography
          variant='bodyMedium'
          color={
            assetClassBranches.reduce(
              (sum, branch) => sum + (branch.targetPercentage || 0),
              0,
            ) === 100
              ? 'success'
              : 'error'
          }
          data-testid={`${testId}-total-value`}
          sx={{ fontWeight: 600 }}
        >
          {assetClassBranches.reduce(
            (sum, branch) => sum + (branch.targetPercentage || 0),
            0,
          )}
          %
        </Typography>
      </Box>
    </Box>
  );
};
